import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Flex, Icon, Text } from "@chakra-ui/react";
import { ChevronRightIcon } from "@chakra-ui/icons";
import { MdFiberManualRecord } from "react-icons/md";
import { getOfferCategoryMeta } from "./offerCategoryMeta";
import { IMG_URL } from "../common/Constant";
const OFFERS_VIDEO_BASE = `${IMG_URL}/offers/offers-page`;
const formatValidityShort = (validFrom, validUntil) => {
    if (!(validFrom === null || validFrom === void 0 ? void 0 : validFrom.length) || !(validUntil === null || validUntil === void 0 ? void 0 : validUntil.length))
        return null;
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    try {
        const startDay = days[new Date(validFrom[0], validFrom[1] - 1, validFrom[2]).getDay()];
        const endDay = days[new Date(validUntil[0], validUntil[1] - 1, validUntil[2]).getDay()];
        if (startDay === endDay)
            return startDay;
        return `${startDay} & ${endDay}`;
    }
    catch {
        return null;
    }
};
const WEEK_ORDER = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];
const DAY_SHORT = {
    MON: "Mon",
    TUE: "Tue",
    WED: "Wed",
    THU: "Thu",
    FRI: "Fri",
    SAT: "Sat",
    SUN: "Sun",
};
const formatTime = (hhmm) => {
    if (!hhmm)
        return null;
    const [hRaw, mRaw] = hhmm.split(":");
    const h = Number(hRaw);
    const m = Number(mRaw);
    if (Number.isNaN(h) || Number.isNaN(m))
        return null;
    const period = h >= 12 ? "PM" : "AM";
    const hour12 = h % 12 === 0 ? 12 : h % 12;
    return m === 0
        ? `${hour12} ${period}`
        : `${hour12}:${String(m).padStart(2, "0")} ${period}`;
};
const isAllDayRange = (start, end) => {
    if (!start || !end)
        return false;
    return ((start === "00:00" || start === "00:00:00") &&
        (end === "23:59" || end === "23:59:59" || end === "24:00"));
};
const formatDays = (rawDays) => {
    if (!rawDays.length)
        return null;
    const set = new Set(rawDays.map((d) => d.toUpperCase()));
    if (set.size === 7)
        return "All Day";
    const weekdays = ["MON", "TUE", "WED", "THU", "FRI"];
    if (weekdays.every((d) => set.has(d)) &&
        !set.has("SAT") &&
        !set.has("SUN")) {
        return "Weekdays";
    }
    if (set.size === 2 && set.has("SAT") && set.has("SUN"))
        return "Weekends";
    const sorted = WEEK_ORDER.filter((d) => set.has(d));
    const indices = sorted.map((d) => WEEK_ORDER.indexOf(d));
    const isContinuous = indices.every((i, idx) => idx === 0 || i === indices[idx - 1] + 1);
    if (isContinuous && sorted.length >= 3) {
        return `${DAY_SHORT[sorted[0]]} to ${DAY_SHORT[sorted[sorted.length - 1]]}`;
    }
    return sorted.map((d) => DAY_SHORT[d]).join(", ");
};
const formatValidityFromMappings = (mappings) => {
    var _a;
    if (!(mappings === null || mappings === void 0 ? void 0 : mappings.length))
        return null;
    const slots = [];
    for (const m of mappings) {
        const ws = (_a = m === null || m === void 0 ? void 0 : m.metadata) === null || _a === void 0 ? void 0 : _a.weekly_slots;
        if (Array.isArray(ws))
            slots.push(...ws);
    }
    if (slots.length === 0)
        return null;
    const days = slots.map((s) => { var _a; return (_a = s.day) !== null && _a !== void 0 ? _a : ""; }).filter(Boolean);
    const daysLabel = formatDays(days);
    // Assume uniform time across slots (matches current API shape).
    const { start, end } = slots[0];
    if (isAllDayRange(start, end))
        return { time: null, days: daysLabel };
    const startLabel = formatTime(start);
    const endLabel = formatTime(end);
    const timeLabel = startLabel && endLabel ? `${startLabel} - ${endLabel}` : null;
    return { time: timeLabel, days: daysLabel };
};
const LOW_INVENTORY_THRESHOLD = 10;
const isCustomImage = (url) => {
    if (!url)
        return false;
    try {
        return !new URL(url).pathname.split("/").includes("default");
    }
    catch {
        return false;
    }
};
const InHouseOfferPreviewCard = ({ offer, onDetailsClick, }) => {
    const meta = getOfferCategoryMeta(offer);
    const validityFromMappings = formatValidityFromMappings(offer.mappings);
    const fallbackValidity = formatValidityShort(offer.valid_from, offer.valid_until);
    const validity = validityFromMappings !== null && validityFromMappings !== void 0 ? validityFromMappings : {
        time: null,
        days: fallbackValidity,
    };
    const hasValidity = !!(validity.time || validity.days);
    const videoSrc = offer.video_url || `${OFFERS_VIDEO_BASE}/${meta.videoFile}`;
    const hasImage = isCustomImage(offer.image);
    const showLowInventory = typeof offer.redemptions_left === "number" &&
        offer.redemptions_left > 0 &&
        offer.redemptions_left < LOW_INVENTORY_THRESHOLD;
    return (_jsxs(Box, { flexShrink: 0, w: "100%", h: ["350px", "380px", "410px"], borderRadius: "20px", overflow: "hidden", bg: "#000000", position: "relative", children: [hasImage ? (_jsxs(_Fragment, { children: [_jsx(Box, { position: "absolute", inset: 0, backgroundImage: `url(${offer.image})`, backgroundSize: "cover", backgroundPosition: "center", zIndex: 0 }), _jsx(Box, { position: "absolute", left: 0, right: 0, bottom: 0, h: "55%", bgGradient: "linear(to-t, #000000 25%, transparent)", zIndex: 1, pointerEvents: "none" })] })) : (_jsxs(_Fragment, { children: [_jsx("video", { src: videoSrc, autoPlay: true, loop: true, muted: true, playsInline: true, style: {
                            position: "absolute",
                            top: 0,
                            right: 0,
                            width: "170px",
                            height: "230px",
                            objectFit: "cover",
                            zIndex: 0,
                        } }), _jsx(Box, { position: "absolute", top: 0, left: 0, bottom: 0, w: "calc(100% - 130px)", bgGradient: "linear(to-r, #000000 50%, transparent)", zIndex: 1, pointerEvents: "none" })] })), _jsxs(Box, { position: "absolute", bottom: "13px", left: "13px", right: "13px", borderRadius: "10px", style: {
                    background: "#FFFFFF14",
                    backgroundBlendMode: "overlay",
                    backdropFilter: "blur(30px)",
                    WebkitBackdropFilter: "blur(30px)",
                    boxShadow: "0px 4px 8px 0px #00000014, -2px -2px 2px -2px #FFFFFF inset, 2px 2px 2px -2px #FFFFFF inset",
                }, p: 4, zIndex: 10, children: [_jsxs(Flex, { align: "center", justify: "space-between", gap: 2, mb: 1, children: [_jsxs(Flex, { align: "center", gap: 2, children: [_jsx(Icon, { as: meta.icon, color: meta.iconColor, boxSize: "14px" }), _jsx(Text, { fontSize: "12px", fontWeight: "400", lineHeight: "12px", letterSpacing: "1px", textTransform: "uppercase", color: "white", children: meta.label })] }), showLowInventory && (_jsx(Box, { px: 2, py: 0.5, borderRadius: "6px", border: "1px solid #FF1E00", bg: "rgba(255,30,0,0.12)", children: _jsxs(Text, { fontSize: "11px", fontWeight: 500, color: "#FF1E00", children: ["Only ", offer.redemptions_left, " left!"] }) }))] }), _jsx(Text, { fontSize: "16px", fontWeight: "700", lineHeight: "22px", textTransform: "uppercase", letterSpacing: "0px", color: "white", mb: 1, noOfLines: 2, children: offer.name }), offer.description && (_jsx(Text, { fontSize: "10px", fontWeight: "400", lineHeight: "14px", color: "#9A9A9A", mb: 3, noOfLines: 2, children: offer.description })), _jsxs(Flex, { justify: "space-between", align: "center", children: [_jsx(Flex, { align: "center", gap: 1.5, fontSize: "11px", color: "rgba(255,255,255,0.7)", children: hasValidity ? (_jsxs(_Fragment, { children: [validity.time && _jsx(Text, { children: validity.time }), validity.time && validity.days && (_jsx(Icon, { as: MdFiberManualRecord, color: "rgba(255,255,255,0.5)", boxSize: "4px" })), validity.days && _jsx(Text, { children: validity.days })] })) : (_jsx(Text, { children: "Today" })) }), _jsxs(Flex, { align: "center", gap: 1, cursor: "pointer", onClick: (e) => {
                                    e.stopPropagation();
                                    onDetailsClick(offer);
                                }, children: [_jsx(Text, { fontSize: "12px", fontWeight: "500", lineHeight: "20px", color: "white", children: "Details" }), _jsx(ChevronRightIcon, { color: "white", boxSize: 3 })] })] })] })] }));
};
export default InHouseOfferPreviewCard;
